import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useHistory, useLocation } from "react-router";

import CircleButton from "@components/buttons/CircleButton";
import styles from "@components/drive/Drive.module.css";
import BodyCopy from "@components/typography/BodyCopy";
import useFileResourceDownloader from "@hooks/useFileResourceDownloader";
import useLazyQueryWithPromise from "@hooks/useLazyQueryWithPromise";
import { Opener } from "@hooks/useOpener";
import {
  FileResourceFragment,
  GetAttachmentDownloadDocument,
  GetAttachmentDownloadQuery,
  GetAttachmentDownloadQueryVariables,
  GetJourneyWithDocumentsQuery,
  GetPdfDownloadDocument,
  GetPdfDownloadQuery,
  GetPdfDownloadQueryVariables
} from "@typing/Generated";
import { getFileImage, isImage, isPdf } from "@utils/fileUtils";
import { imageUrl } from "@utils/imageUtils";
import { getFileResourceTitle } from "@utils/modelUtils";

type Props = {
  fileResource: FileResourceFragment | null;
  journey: GetJourneyWithDocumentsQuery["journey"];
  opener: Opener;
};

const DocumentPreview = ({ fileResource, journey, opener }: Props) => {
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();
  const fileResourceDownloader = useFileResourceDownloader();

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewUnavailable, setPreviewUnavailable] = useState<boolean>(false);

  useEffect(() => {
    if (opener.isOpen) {
      history.replace({
        hash: location.hash,
        search: "hideFab=true"
      });
    } else {
      history.replace({
        hash: location.hash,
        search: ""
      });
    }
  }, [history, location.hash, opener.isOpen]);

  const getPdfDownload = useLazyQueryWithPromise<GetPdfDownloadQuery, GetPdfDownloadQueryVariables>(
    GetPdfDownloadDocument
  );

  const attachmentFetch = useLazyQueryWithPromise<GetAttachmentDownloadQuery, GetAttachmentDownloadQueryVariables>(
    GetAttachmentDownloadDocument
  );

  useEffect(() => {
    setPreviewUrl(null);
    setPreviewUnavailable(false);
  }, [fileResource]);

  useEffect(() => {
    if (fileResource && !isImage(fileResource) && !isPdf(fileResource) && !fileResource.pdfType) {
      setPreviewUnavailable(true);
    }

    if (previewUrl || !fileResource || (!isImage(fileResource) && !isPdf(fileResource) && !fileResource.pdfType)) {
      return;
    }

    setPreviewUnavailable(false);

    if (fileResource?.attachment || fileResource.pdfType) {
      if (fileResource.pdfType) {
        getPdfDownload({
          fetchPolicy: "no-cache",
          variables: { journeyId: journey.id, type: fileResource.pdfType }
        }).then(response => {
          fetch(response.data.pdfDownload.url).then(response => {
            response.blob().then(blob => {
              const url = URL.createObjectURL(blob);
              setPreviewUrl(`${url}#toolbar=0&navpanes=0`);
            });
          });
        });
      } else if (fileResource.attachment) {
        attachmentFetch({ fetchPolicy: "no-cache", variables: { id: fileResource.attachment?.id } }).then(response => {
          if (response.data.attachmentDownload.url && response.data.attachmentDownload.filename) {
            fetch(response.data.attachmentDownload.url).then(response => {
              response.blob().then(blob => {
                const url = URL.createObjectURL(blob);
                setPreviewUrl(`${url}#toolbar=0&navpanes=0`);
              });
            });
          }
        });
      }
    }
  }, [attachmentFetch, fileResource, getPdfDownload, journey.id, previewUrl]);

  if (!fileResource || !opener.isOpen) {
    return null;
  }

  return (
    <div className={styles.documentPreviewFullScreen}>
      <div className={styles.documentPreviewBackdrop} onClick={opener.close}></div>
      <div className={styles.documentPreviewContainer}>
        <div className={styles.documentPreview}>
          <div className={styles.documentPreviewTitleRow}>
            <img
              aria-label={intl.formatMessage({ id: "accessibility.ariaLabels.file" })}
              className={styles.headerFileImg}
              src={imageUrl(getFileImage(fileResource))}
            />
            <div className={styles.headerFileTitle}>
              <BodyCopy bold color="gray-500">
                {getFileResourceTitle(fileResource, intl)}
              </BodyCopy>
            </div>
            <div>
              <CircleButton
                ariaLabelKey="accessibility.ariaLabels.download"
                color="gray-500"
                fill="clear"
                icon="download"
                onClick={() => fileResourceDownloader.handleDownload(fileResource, journey.id)}
                size="medium"
              />
              <CircleButton
                ariaLabelKey="accessibility.ariaLabels.close"
                color="gray-500"
                fill="clear"
                icon="times"
                onClick={opener.close}
                size="medium"
              />
            </div>
          </div>
          <div className={styles.documentPreviewPreview}>
            {previewUnavailable ? (
              <BodyCopy>
                <FormattedMessage id="pages.drive.previewUnavailable" />
              </BodyCopy>
            ) : (
              <>
                {(isPdf(fileResource) || fileResource.pdfType) && previewUrl && <iframe src={previewUrl} />}
                {isImage(fileResource) && previewUrl && (
                  <img aria-label={intl.formatMessage({ id: "accessibility.ariaLabels.file" })} src={previewUrl} />
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentPreview;
